/**
 * @fileoverview The mailbox rental vocabulary: a physical box's status (`mailboxes.status`), the rental agreement that
 * holds it (`mailbox_rentals.status`), the box types, and the billing periods with their terms. Declared once for
 * pos-api's routes, models and import, and pos-web's mailbox screens (L-R7CLOSE row 54, owner ruling Q-R8CLOSE-39).
 *
 * ## Two axes
 *
 * A BOX is the physical slot at a store. A RENTAL is one agreement that holds it: who, from when, on what period and
 * rate, paid through when. A box has at most one active rental and any number of ended ones: its history. PostalMate
 * kept one row per agreement, and the import copied that as boxes, so box 214 appeared once per past renter. The
 * owner's ruling on the re-asked Q3 ("One box, full history") separates the two.
 *
 * ## The box machine
 *
 * The rental commands own every edge that changes who holds a box ({@link RENTAL_OWNED_EDGES}):
 *
 * - start: `available → rented`;
 * - renew: `rented → rented` (a declared self-edge; it moves the due date) and `past_due → rented`;
 * - end: `rented → available` and `past_due → available`.
 *
 * The operator's status route owns the rest: the manual delinquency mark `rented → past_due`, and the operational set
 * among `available`, `maintenance`, `unavailable` and `closed` (a box taken out of service; `closed → available`
 * reopens it). Every status is a KEY, a terminal one mapping to `[]`, the `SHIPMENT_STATUS_TRANSITIONS` convention.
 *
 * ## The rental machine
 *
 * Only what a writer produces: a start or an imported current agreement writes `active`; a renewal is the self-edge
 * `active → active`; an end, or an imported closed agreement, writes `ended`, which is terminal.
 *
 * @module @puffin-core/status-taxonomy/mailboxRental
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/** The empty answer, shared so a caller can never widen a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * A physical box's status.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_STATUS = Object.freeze({
  AVAILABLE: 'available',
  RENTED: 'rented',
  PAST_DUE: 'past_due',
  MAINTENANCE: 'maintenance',
  UNAVAILABLE: 'unavailable',
  CLOSED: 'closed',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_STATUS_VALUES = Object.freeze(Object.values(MAILBOX_STATUS));

const { AVAILABLE, RENTED, PAST_DUE, MAINTENANCE, UNAVAILABLE, CLOSED } = MAILBOX_STATUS;

/**
 * Every legal box move, spelled once.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const MAILBOX_STATUS_TRANSITIONS = Object.freeze({
  [AVAILABLE]: Object.freeze([RENTED, MAINTENANCE, UNAVAILABLE, CLOSED]),
  [RENTED]: Object.freeze([RENTED, PAST_DUE, AVAILABLE]),
  [PAST_DUE]: Object.freeze([RENTED, AVAILABLE]),
  [MAINTENANCE]: Object.freeze([AVAILABLE, UNAVAILABLE, CLOSED]),
  [UNAVAILABLE]: Object.freeze([AVAILABLE, MAINTENANCE, CLOSED]),
  [CLOSED]: Object.freeze([AVAILABLE]),
});

/**
 * The statuses at which a rental HOLDS the box: renew and end guard the box on them, and a held box offers Renew,
 * End rental and View rental (MAILBOX-SIGNUP-JOURNEY-001 C4; promoted from pos-api `MailboxRentalService`'s private
 * `HELD_STATUSES`, so the server's guard and pos-web's row actions read one list).
 *
 * @type {ReadonlyArray<string>}
 */
const MAILBOX_HELD_STATUSES = Object.freeze([RENTED, PAST_DUE]);

/**
 * Whether a box at `status` is held by a rental.
 *
 * @param {*} status - The box's status, as read.
 * @returns {boolean} True for a held status; false for every other value.
 */
function isMailboxHeld(status) {
  return MAILBOX_HELD_STATUSES.includes(status);
}

/**
 * The box moves only a rental command may make (start, renew, end): each changes who holds the box or when they
 * are paid through, so the status route refuses them by name.
 *
 * @type {ReadonlyArray<ReadonlyArray<string>>}
 */
const RENTAL_OWNED_EDGES = Object.freeze([
  Object.freeze([AVAILABLE, RENTED]),
  Object.freeze([RENTED, RENTED]),
  Object.freeze([RENTED, AVAILABLE]),
  Object.freeze([PAST_DUE, RENTED]),
  Object.freeze([PAST_DUE, AVAILABLE]),
]);

/**
 * Whether `from → to` belongs to the rental commands.
 *
 * @param {string} from - The box's current status.
 * @param {string} to - The requested status.
 * @returns {boolean} True when only start / renew / end may make this move.
 */
function isRentalOwnedEdge(from, to) {
  return RENTAL_OWNED_EDGES.some(([edgeFrom, edgeTo]) => edgeFrom === from && edgeTo === to);
}

/**
 * The statuses a box at `status` may move to next.
 *
 * Reads OWN keys only, so an unknown value (or an inherited `Object.prototype` name such as `constructor`) answers
 * `[]` rather than a function.
 *
 * @param {string} status - The box's current status.
 * @param {{ operator?: boolean }} [options] - `operator: true` answers only the status route's moves, never a
 *   rental-owned edge.
 * @returns {ReadonlyArray<string>} The legal targets, frozen; `[]` when terminal or unknown.
 */
function mailboxStatusTargets(status, { operator = false } = {}) {
  if (
    typeof status !== 'string' ||
    !Object.prototype.hasOwnProperty.call(MAILBOX_STATUS_TRANSITIONS, status)
  ) {
    return NO_TARGETS;
  }
  const targets = MAILBOX_STATUS_TRANSITIONS[status];
  return operator ? Object.freeze(targets.filter((to) => !isRentalOwnedEdge(status, to))) : targets;
}

/**
 * Each box status's badge TONE (`<StatusBadgeCell statusTones>`, Q-MI-25): a free box is good news, a held one is
 * information, a past-due one needs attention, and a box out of service or retired is closed without a verdict
 * (`neutral` — out-of-service was the muted, non-tone `badge-muted`; the closed tone set has one no-verdict tone).
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_STATUS_BADGE_TONES = Object.freeze({
  [AVAILABLE]: BADGE_TONE.SUCCESS,
  [RENTED]: BADGE_TONE.INFO,
  [PAST_DUE]: BADGE_TONE.WARNING,
  [MAINTENANCE]: BADGE_TONE.NEUTRAL,
  [UNAVAILABLE]: BADGE_TONE.NEUTRAL,
  [CLOSED]: BADGE_TONE.NEUTRAL,
});

/**
 * A rental agreement's status.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_RENTAL_STATUS = Object.freeze({
  ACTIVE: 'active',
  ENDED: 'ended',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_RENTAL_STATUS_VALUES = Object.freeze(Object.values(MAILBOX_RENTAL_STATUS));

/**
 * Every legal rental move: renewal is the self-edge, and an ended rental is history.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const MAILBOX_RENTAL_STATUS_TRANSITIONS = Object.freeze({
  [MAILBOX_RENTAL_STATUS.ACTIVE]: Object.freeze([
    MAILBOX_RENTAL_STATUS.ACTIVE,
    MAILBOX_RENTAL_STATUS.ENDED,
  ]),
  [MAILBOX_RENTAL_STATUS.ENDED]: NO_TARGETS,
});

/** A rental's badge TONE (`<StatusBadgeCell statusTones>`, Q-MI-25). @type {Readonly<Object<string, string>>} */
const MAILBOX_RENTAL_STATUS_BADGE_TONES = Object.freeze({
  [MAILBOX_RENTAL_STATUS.ACTIVE]: BADGE_TONE.SUCCESS,
  [MAILBOX_RENTAL_STATUS.ENDED]: BADGE_TONE.NEUTRAL,
});

/**
 * The box types a store installs.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_TYPE = Object.freeze({
  STANDARD: 'standard',
  LARGE: 'large',
  KEY: 'key',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_TYPE_VALUES = Object.freeze(Object.values(MAILBOX_TYPE));

/**
 * How often a rental is renewed. `semiannual`, never `biannual` (which English reads both as twice a year and as
 * every two years).
 *
 * @type {Readonly<Object<string, string>>}
 */
const RENTAL_BILLING_PERIOD = Object.freeze({
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  SEMIANNUAL: 'semiannual',
  ANNUAL: 'annual',
});

/** @type {ReadonlyArray<string>} */
const RENTAL_BILLING_PERIOD_VALUES = Object.freeze(Object.values(RENTAL_BILLING_PERIOD));

/**
 * Each billing period's term in whole months: a start or a renewal moves the due date by exactly this many months
 * from its anchor day.
 *
 * @type {Readonly<Object<string, number>>}
 */
const RENTAL_TERM_MONTHS = Object.freeze({
  [RENTAL_BILLING_PERIOD.MONTHLY]: 1,
  [RENTAL_BILLING_PERIOD.QUARTERLY]: 3,
  [RENTAL_BILLING_PERIOD.SEMIANNUAL]: 6,
  [RENTAL_BILLING_PERIOD.ANNUAL]: 12,
});

/**
 * Why a rental ended: the end route judges it and the audit records it; pos-web's End rental confirm offers it, in
 * this order (MAILBOX-SIGNUP-JOURNEY-001 C4; promoted from pos-api's `mailboxRequestSchemas` `RENTAL_END_REASONS`,
 * which the confirm could only have copied).
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAILBOX_RENTAL_END_REASON = Object.freeze({
  CUSTOMER_REQUEST: 'customer_request',
  NON_PAYMENT: 'non_payment',
  BOX_CLOSED: 'box_closed',
  OTHER: 'other',
});

/** @type {ReadonlyArray<string>} */
const MAILBOX_RENTAL_END_REASON_VALUES = Object.freeze(Object.values(MAILBOX_RENTAL_END_REASON));

module.exports = {
  MAILBOX_STATUS,
  MAILBOX_STATUS_VALUES,
  MAILBOX_STATUS_TRANSITIONS,
  MAILBOX_STATUS_BADGE_TONES,
  MAILBOX_HELD_STATUSES,
  isMailboxHeld,
  RENTAL_OWNED_EDGES,
  isRentalOwnedEdge,
  mailboxStatusTargets,
  MAILBOX_RENTAL_STATUS,
  MAILBOX_RENTAL_STATUS_VALUES,
  MAILBOX_RENTAL_STATUS_TRANSITIONS,
  MAILBOX_RENTAL_STATUS_BADGE_TONES,
  MAILBOX_RENTAL_END_REASON,
  MAILBOX_RENTAL_END_REASON_VALUES,
  MAILBOX_TYPE,
  MAILBOX_TYPE_VALUES,
  RENTAL_BILLING_PERIOD,
  RENTAL_BILLING_PERIOD_VALUES,
  RENTAL_TERM_MONTHS,
};
